import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import { getSupabase } from "../utils";
import type { User } from "@/lib/types/user";

// ============================================================================
// ADMIN USERS — WRITE HOOKS (create / update / delete)
// ============================================================================

/** Shared select fragment for app_users with role join */
const USER_SELECT = `
  *,
  roles:role_id (
    id,
    name,
    description
  )
`;

/** Fields that must not be written back to app_users directly */
const RELATION_FIELDS = [
  "roles",
  "app_users",
  "contacts",
  "tickets",
  "time_entries",
];

/**
 * Create a new user.
 */
export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = getSupabase();

  return useMutation<User, Error, Partial<User>>({
    mutationFn: async (userData) => {
      if (!appUser) throw new Error("Authentication required");

      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      const processedUserData = { ...userData };

      // Convert role name → role_id if needed
      if (userData.role && !userData.role_id) {
        const { data: roleData, error: roleError } = await supabase
          .from("roles")
          .select("id")
          .eq("name", userData.role)
          .single();

        if (roleError) throw new Error(`Invalid role: ${userData.role}`);
        processedUserData.role_id = roleData.id;
        delete processedUserData.role;
      }

      const { data, error } = await supabase
        .from("app_users")
        .insert(processedUserData)
        .select(USER_SELECT)
        .single();

      if (error) throw new Error(`Failed to create user: ${error.message}`);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User created successfully");
    },
    onError: (error) => {
      toast.error(`Failed to create user: ${error.message}`);
    },
  });
}

/**
 * Update an existing user.
 */
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = getSupabase();

  return useMutation<User, Error, { id: string; updates: Partial<User> }>({
    mutationFn: async ({ id, updates }) => {
      if (!appUser) throw new Error("Authentication required");

      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      const processedUpdates = { ...updates };

      // Strip relation fields
      RELATION_FIELDS.forEach((field) => {
        delete processedUpdates[field as keyof typeof processedUpdates];
      });

      // Convert role name → role_id if needed
      if (updates.role && !updates.role_id) {
        const { data: roleData, error: roleError } = await supabase
          .from("roles")
          .select("id")
          .eq("name", updates.role)
          .single();

        if (roleError) throw new Error(`Invalid role: ${updates.role}`);
        processedUpdates.role_id = roleData.id;
        delete processedUpdates.role;
      }

      const { error } = await supabase
        .from("app_users")
        .update(processedUpdates)
        .eq("id", id);

      if (error) throw new Error(`Failed to update user: ${error.message}`);

      const { data, error: fetchError } = await supabase
        .from("app_users")
        .select(USER_SELECT)
        .eq("id", id)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch updated user: ${fetchError.message}`);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-user", data.id] });
      toast.success("User updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });
}

/**
 * Delete a user (admin-only; blocked if user has tickets or time entries).
 */
export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = getSupabase();

  return useMutation<string, Error, string>({
    mutationFn: async (userId) => {
      if (!appUser) throw new Error("Authentication required");

      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_admin"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      const { data: tickets } = await supabase
        .from("tickets")
        .select("id")
        .eq("assigned_to", userId)
        .limit(1);

      if (tickets && tickets.length > 0) {
        throw new Error("Cannot delete user with assigned tickets");
      }

      const { data: timeEntries } = await supabase
        .from("time_entries")
        .select("id")
        .eq("user_id", userId)
        .limit(1);

      if (timeEntries && timeEntries.length > 0) {
        throw new Error("Cannot delete user with time entries");
      }

      const { error } = await supabase
        .from("app_users")
        .delete()
        .eq("id", userId);

      if (error) throw new Error(`Failed to delete user: ${error.message}`);
      return userId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });
}
